/**
 * Company → Location nomenclature.
 *
 * Accounts that purchased CMMS run maintenance on sites they own, so the
 * "Company" module reads as "Location" for them. Accounts on Field Service
 * service other businesses and keep "Company". The two products are mutually
 * exclusive (see `conflictingPurchases` in the backend plan config), so the
 * presence of `cmms` in `installedProducts` is the whole switch.
 *
 * Every Location string is written out, in `lang/<locale>/location.json`,
 * keyed exactly like the catalogue it overrides. This file only merges the two.
 *
 * Deriving them with rewrite rules was tried and produced broken German:
 * "Standort" is masculine where "Firma" is feminine and "Unternehmen" neuter,
 * so the articles, adjective endings and pronouns all move with the noun, and
 * no rewrite rule gets that right. Writing them out also means a string with no
 * override simply keeps its wording, which is what the settings pages that
 * describe the OEM's *own* company need — no exclusion list to maintain.
 */

/**
 * The one product type this module cares about. Declared here rather than
 * imported from `paidFeaturesHierarchy` so the module stays free of the
 * `$/settings` build alias and can be loaded from anywhere; that file holds
 * the full product map.
 */
const CMMS = "cmms";

export type InstalledProduct = { type?: string | null; tier?: string | null };

export const NOMENCLATURE = {
  COMPANY: "company",
  LOCATION: "location",
} as const;

export type Nomenclature = (typeof NOMENCLATURE)[keyof typeof NOMENCLATURE];

/** `true` when the OEM bought CMMS, i.e. when "Company" should read "Location". */
export const isLocationNomenclature = (
  installedProducts?: InstalledProduct[] | null,
): boolean =>
  (installedProducts ?? []).some((product) => product?.type === CMMS);

export const getNomenclature = (
  installedProducts?: InstalledProduct[] | null,
): Nomenclature =>
  isLocationNomenclature(installedProducts)
    ? NOMENCLATURE.LOCATION
    : NOMENCLATURE.COMPANY;

type MessageNode = string | { [key: string]: MessageNode };

/**
 * Lays the Location catalogue over the base one. Only the keys present in the
 * override move; everything else keeps its own wording, which is what makes the
 * override file double as the list of what gets renamed.
 */
export const applyNomenclatureToMessages = <T extends object>(
  messages: T,
  overrides?: object | null,
): T => {
  if (!overrides) return messages;

  return Object.entries(messages as Record<string, MessageNode>).reduce<
    Record<string, MessageNode>
  >((acc, [key, value]) => {
    const override = (overrides as Record<string, MessageNode>)[key];

    if (override === undefined) acc[key] = value;
    else if (typeof value === "string" || typeof override === "string")
      acc[key] = override;
    else acc[key] = applyNomenclatureToMessages(value, override);

    return acc;
  }, {}) as T;
};

/**
 * The Company module's route. CMMS accounts browse `/locations`; the
 * `/companies` path stays registered for everyone so existing links and
 * bookmarks keep resolving.
 */
export const COMPANY_BASE_PATH = "/app/asset-hub/companies";
export const LOCATION_BASE_PATH = "/app/asset-hub/locations";

export const getCompanyBasePath = (locationNomenclature: boolean): string =>
  locationNomenclature ? LOCATION_BASE_PATH : COMPANY_BASE_PATH;

/**
 * Rewrites an English literal built in code. The catalogue is handled by the
 * merge above; this covers only the `?? "Company ..."` fallbacks, which are
 * always English and carry no grammar.
 */
export const applyNomenclatureToText = (text: string): string =>
  text
    .replace(/\bCOMPANIES\b/g, "LOCATIONS")
    .replace(/\bCOMPANY\b/g, "LOCATION")
    .replace(/\bCompanies\b/g, "Locations")
    .replace(/\bcompanies\b/g, "locations")
    .replace(/\bCompany\b/g, "Location")
    .replace(/\bcompany\b/g, "location");
